import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { NotFoundError, UnderReviewError } from '#src/shared/error';

import { getViewerSignature } from '#src/app/shared/api/signature';
import { differenceInDays } from '#src/app/shared/utils/dates';

import type { DecisionMakerResponseDataQuery, DecisionMakerResponseDataQueryVariables } from './api.graphql';
import { DecisionMakerResponseData } from './api.graphql';

export type DecisionMakerResponsePetition = NonNullable<
	NonNullable<DecisionMakerResponseDataQuery['decisionMakerResponse']>['petition']
>;
export type DecisionMakerResponse = Omit<
	NonNullable<DecisionMakerResponseDataQuery['decisionMakerResponse']>,
	'petition'
> & {
	petition: DecisionMakerResponsePetition;
};
export type DecisionMakerResponseAuthor = DecisionMakerResponse['decisionMaker'];
export type DecisionMakerResponsePageData = {
	decisionMakerResponse: DecisionMakerResponse;
	petitionSigned: boolean;
	petitionCreatedLessThanADayAgo: boolean;
};

export async function getDecisionMakerResponsePageData(
	utilityContext: UtilityContext,
	{
		petitionSlugOrId,
		responseId,
		userId,
		countryCode,
		locale,
	}: { petitionSlugOrId: string; responseId: string; userId?: string; countryCode: string; locale: string },
): Promise<DecisionMakerResponsePageData> {
	const {
		gql: { fetch },
	} = utilityContext;

	const [{ data }, signature] = await Promise.all([
		fetch<DecisionMakerResponseDataQuery, DecisionMakerResponseDataQueryVariables>({
			query: DecisionMakerResponseData,
			variables: {
				responseId,
				petitionSlugOrId,
				countryCode,
				locale,
			},
			rejectOnError: true,
		}),
		userId ? getViewerSignature(petitionSlugOrId, utilityContext) : Promise.resolve(undefined),
	]);

	if (!data.decisionMakerResponse?.petition) {
		throw new NotFoundError();
	}

	if (
		data.decisionMakerResponse.petition.status === 'UNDER_REVIEW' &&
		!data.decisionMakerResponse.petition.viewerCanEdit
	) {
		throw new UnderReviewError();
	}

	return {
		decisionMakerResponse: {
			...data.decisionMakerResponse,
			petition: data.decisionMakerResponse.petition,
		},
		petitionSigned: !!signature,

		petitionCreatedLessThanADayAgo:
			differenceInDays(new Date(), new Date(data.decisionMakerResponse.petition.createdAt)) < 1,
	};
}
